/**
 * Filter bar: active groups, result count, reset, and optional configure panel.
 */

import * as React from 'react';
import { X, Save, RotateCcw } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { cn } from '@/lib/utils';
import type { FilterGroup } from '@/types/data-table.types';

/** Props for {@link DataTableFilters}. */
interface DataTableFiltersProps {
  show: boolean;
  hasActiveFilters: boolean;
  filterGroups: FilterGroup[];
  onClearFilters: () => void;
  onSaveAsDefault?: () => void;
  resultCount: number;
  resultLabel: string;
  isSaving?: boolean;
  /** Show the configure button in the filter panel header. */
  onToggleConfigure?: () => void;
  showConfigure?: boolean;
  /** Filter-visibility toggles rendered in the configure panel. */
  configureContent?: React.ReactNode;
  /** `collapse` (inline panel, default) or `modal`. */
  mode?: 'collapse' | 'modal';
  /** Close handler when `mode='modal'`. */
  onClose?: () => void;
}

/** True when the group heading is worth showing (skip the generic "Filtres" wrapper). */
function hasFilterGroupHeading(label: string | undefined): boolean {
  return Boolean(label?.trim()) && label !== 'Filtres';
}

/** One filter group: optional heading + custom or option content. */
function renderFilterGroup(group: FilterGroup): React.ReactNode {
  const body =
    group.type === 'custom' ? (
      (group.customContent ?? group.customRender?.())
    ) : (
      <div className="filter-options">{group.customRender?.()}</div>
    );

  return (
    <div key={group.id} className="filter-group">
      {hasFilterGroupHeading(group.label) ? (
        <span className="filter-group-label">{group.label}</span>
      ) : null}
      {body}
    </div>
  );
}

/** Filter bar for a data table. */
export const DataTableFilters: React.FC<DataTableFiltersProps> = ({
  show,
  hasActiveFilters,
  filterGroups,
  onClearFilters,
  onSaveAsDefault,
  resultCount,
  resultLabel,
  isSaving = false,
  onToggleConfigure,
  showConfigure = false,
  configureContent,
  mode = 'collapse',
  onClose,
}) => {
  if (!show) return null;

  const filterContent = (
    <>
      <div className="filter-panel-header">
        <div className="filter-groups-container">{filterGroups.map(renderFilterGroup)}</div>

        {onToggleConfigure && (
          <Button
            preset="filter-configure"
            className={cn(showConfigure && 'section-action-button-active')}
            onClick={onToggleConfigure}
          />
        )}
      </div>

      {showConfigure && configureContent && (
        <div className="filter-configure-panel">
          <div className="filter-configure-separator" />
          <div className="filter-configure-content">{configureContent}</div>
        </div>
      )}

      <div className="filter-actions">
        <div className="filter-result-count">
          {resultCount} {resultLabel} trouvé{resultCount === 1 ? '' : 's'}
        </div>
        <div className="flex gap-2">
          {mode === 'modal' && onClose && (
            <Button variant="primary" size="sm" onClick={onClose} icon={X} iconPosition="left">
              Fermer
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={onClearFilters}
            icon={RotateCcw}
            iconPosition="left"
            disabled={!hasActiveFilters}
          >
            Réinitialiser
          </Button>
          {onSaveAsDefault && mode !== 'modal' && (
            <Button
              variant="primary"
              size="sm"
              onClick={onSaveAsDefault}
              icon={Save}
              iconPosition="left"
              loading={isSaving}
            >
              Sauvegarder par défaut
            </Button>
          )}
        </div>
      </div>
    </>
  );

  if (mode === 'modal') {
    return (
      <BaseModal
        open={show}
        onClose={onClose || (() => {})}
        size="xl"
        header={{
          title: 'Filtres',
          titleSize: 'lg',
          titleWeight: 'semibold',
        }}
        hideContentWrapper
      >
        <div className="filters-panel">{filterContent}</div>
      </BaseModal>
    );
  }

  return <div className="filters-panel animate-fade-in">{filterContent}</div>;
};
